import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { API, BACKEND_URL } from '../config/api';
import { typeLabel, normalizeGender } from '../config/horseOptions';
import { useAuth } from '../context/AuthContext';
import { CountdownTimer } from '../components/CountdownTimer';
import BiddingPanelInline from '../components/BiddingPanelInline';
import LoadingSpinner from '../components/LoadingSpinner';

const BIDDER_COLORS = [
  '#0a7e8c', '#e74c3c', '#2ecc71', '#9b59b6', '#f39c12',
  '#1abc9c', '#e67e22', '#3498db', '#e91e63', '#00bcd4',
  '#8bc34a', '#ff5722', '#673ab7', '#009688', '#ff9800',
  '#795548', '#607d8b', '#4caf50', '#f44336', '#2196f3',
  '#c2185b', '#00695c', '#4527a0', '#bf360c', '#0277bd',
  '#558b2f', '#ad1457', '#1565c0', '#6a1b9a', '#e65100',
  '#2e7d32', '#283593', '#d84315', '#00838f', '#9e9d24',
  '#4e342e', '#37474f', '#c62828', '#1b5e20', '#4a148c',
  '#ff6f00', '#006064', '#880e4f', '#33691e', '#01579b',
  '#3e2723', '#827717', '#b71c1c', '#004d40', '#311b92'
];

const getBidderColor = (bidderNumber) => {
  if (!bidderNumber) return BIDDER_COLORS[0];
  return BIDDER_COLORS[(bidderNumber - 1) % BIDDER_COLORS.length];
};

const AuctionDetailPage = () => {
  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bidAmount, setBidAmount] = useState(0);
  const [bidding, setBidding] = useState(false);
  const [error, setError] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [notification, setNotification] = useState(null);
  const [autobidMax, setAutobidMax] = useState('');
  const [autobidActive, setAutobidActive] = useState(null);
  const [showAutobid, setShowAutobid] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const auctionId = location.pathname.split('/').pop();
  const wsRef = useRef(null);
  const notifTimerRef = useRef(null);

  const fetchAuction = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auctions/${auctionId}`);
      setAuction(response.data);
      if (!bidAmount || bidAmount < response.data.current_price + response.data.min_increment) {
        setBidAmount(response.data.current_price + response.data.min_increment);
      }
    } catch (error) {
      console.error('Error fetching auction:', error);
    } finally {
      setLoading(false);
    }
  }, [auctionId]);

  // Fetch autobid status
  useEffect(() => {
    if (user) {
      axios.get(`${API}/auctions/${auctionId}/autobid`).then(res => {
        setAutobidActive(res.data.autobid);
      }).catch(() => {});
    }
  }, [auctionId, user]);

  // WebSocket connection
  useEffect(() => {
    fetchAuction();
    
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = BACKEND_URL.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}//${wsHost}/api/ws/auction/${auctionId}`;
    
    let ws = null;
    let pingInterval = null;
    
    const connect = () => {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => {
        console.log('WebSocket connected');
        pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, 30000);
      };
      
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'new_bid') {
          // Update auction state
          setAuction(prev => {
            if (!prev) return prev;
            const newBid = data.bid;
            const existingBids = prev.bids || [];
            return {
              ...prev,
              current_price: data.auction.current_price,
              total_bids: data.auction.total_bids,
              end_time: data.auction.end_time,
              winner_id: data.auction.winner_id,
              bids: existingBids.some(b => b.bid_id === newBid.bid_id) ? existingBids : [newBid, ...existingBids]
            };
          });
          // Update bid amount to new minimum
          setBidAmount(data.auction.current_price + (auction?.min_increment || 100));
          // Show notification
          setNotification({
            bidder_number: data.bid.bidder_number,
            amount: data.bid.amount,
            is_autobid: data.bid.is_autobid,
            horse_name: data.bid.horse_name || auction?.horse?.name || ''
          });
          clearTimeout(notifTimerRef.current);
          notifTimerRef.current = setTimeout(() => setNotification(null), 5000);
        }
      };
      
      ws.onclose = () => {
        clearInterval(pingInterval);
        // Reconnect after 3 seconds
        setTimeout(connect, 3000);
      };
      
      ws.onerror = () => ws.close();
    };
    
    connect();
    
    // Fallback polling every 10s
    const pollInterval = setInterval(fetchAuction, 10000);
    
    return () => {
      clearInterval(pollInterval);
      clearInterval(pingInterval);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
    };
  }, [auctionId, fetchAuction]);

  const incrementBid = () => {
    const step = auction?.min_increment || 100;
    setBidAmount(prev => prev + step);
  };

  const decrementBid = () => {
    const min = (auction?.current_price || 0) + (auction?.min_increment || 100);
    const step = auction?.min_increment || 100;
    setBidAmount(prev => Math.max(min, prev - step));
  };

  const handleBid = async () => {
    setError('');
    setShowConfirm(false);
    
    if (!user) {
      window.location.href = '/login';
      return;
    }

    if (!user.is_verified) {
      setError('Il tuo account non è verificato. Invia i documenti a info@equusprime.it');
      return;
    }

    setBidding(true);
    try {
      await axios.post(`${API}/auctions/${auctionId}/bid`, {
        auction_id: auctionId,
        amount: bidAmount
      });
      fetchAuction();
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore durante l\'offerta');
    } finally {
      setBidding(false);
    }
  };

  const handleAutobid = async () => {
    if (!user) { window.location.href = '/login'; return; }
    if (!user.is_verified) { setError('Account non verificato'); return; }
    
    try {
      const res = await axios.post(`${API}/auctions/${auctionId}/autobid`, {
        auction_id: auctionId,
        max_amount: parseFloat(autobidMax)
      });
      setAutobidActive({ max_amount: parseFloat(autobidMax), is_active: true });
      setShowAutobid(false);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore autobid');
    }
  };

  const cancelAutobid = async () => {
    try {
      await axios.delete(`${API}/auctions/${auctionId}/autobid`);
      setAutobidActive(null);
    } catch (err) {
      setError('Errore cancellazione autobid');
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!auction) return <div className="container-custom py-20 text-center">Asta non trovata</div>;

  const minBid = auction.current_price + auction.min_increment;
  const horse = auction.horse;
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <div>
      {/* Breadcrumb */}
      <div className="bg-gray-50 py-3">
        <div className="container-custom px-4 text-sm text-[#64748b]">
          <a href="/" className="hover:text-[#c9a227]">Home</a> / <a href="/aste" className="hover:text-[#c9a227]">Aste</a> / <span className="text-[#0a1628]">{horse?.name}</span>
        </div>
      </div>

      <div className="section">
        <div className="container-custom px-4">
          {/* Horse Name + Type + Basic Info */}
          <div className="mb-6 md:mb-8">
            <h1 className="font-display text-3xl md:text-5xl mb-2" data-testid="horse-name">{horse?.name}</h1>
            {horse?.horse_type && (
              <span className="inline-block text-sm font-bold text-[#c9a227] uppercase tracking-wider" data-testid="detail-horse-type">
                {typeLabel(horse.horse_type)}
                {horse.pregnant_with && ` – incinta di ${horse.pregnant_with}`}
              </span>
            )}
            <p className="text-[#64748b] mt-2">
              {normalizeGender(horse?.gender)} {horse?.birth_year ? `n.${horse.birth_year} (${horse.age} anni)` : `${horse?.age} anni`}
            </p>
            {/* Lineage */}
            {(horse?.father || horse?.mother) && (
              <p className="text-[#0a1628] mt-1 font-medium">
                {horse.father}{horse.mother ? ` – ${horse.mother}` : ''}{horse.grandfather ? ` (${horse.grandfather})` : ''}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
            {/* LEFT: Images + Description + Facts + Documents + Contact */}
            <div className="lg:col-span-7">
              {/* Main Image */}
              {horse?.images?.[0] ? (
                <img 
                  src={horse.images[0]} 
                  alt={horse?.name}
                  className="w-full h-[280px] sm:h-[400px] lg:h-[500px] object-cover rounded-lg"
                  data-testid="horse-main-image"
                />
              ) : (
                <div className="w-full h-[280px] sm:h-[400px] lg:h-[500px] bg-gray-200 flex items-center justify-center rounded-lg">
                  <span className="text-gray-400">Nessuna immagine</span>
                </div>
              )}
              {/* Thumbnail Gallery */}
              {horse?.images?.length > 1 && (
                <div className="grid grid-cols-4 gap-2 md:gap-3 mt-3">
                  {horse.images.slice(1, 5).map((img, idx) => (
                    <img key={idx} src={img} alt="" className="w-full h-16 md:h-24 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity" />
                  ))}
                </div>
              )}

              {/* MOBILE ONLY: Bidding Panel right after photo */}
              <div className="lg:hidden mt-4">
                <BiddingPanelInline
                  auction={auction}
                  bidAmount={bidAmount}
                  setBidAmount={setBidAmount}
                  minBid={minBid}
                  incrementBid={incrementBid}
                  decrementBid={decrementBid}
                  bidding={bidding}
                  error={error}
                  setError={setError}
                  setShowConfirm={setShowConfirm}
                  user={user}
                  showAutobid={showAutobid}
                  setShowAutobid={setShowAutobid}
                  autobidActive={autobidActive}
                  autobidMax={autobidMax}
                  setAutobidMax={setAutobidMax}
                  handleAutobid={handleAutobid}
                  cancelAutobid={cancelAutobid}
                  fetchAuction={fetchAuction}
                />
              </div>

              {/* Description */}
              <div className="mt-6 md:mt-8">
                <h2 className="font-display text-xl md:text-2xl mb-4">Descrizione</h2>
                <p className="text-[#0a1628] leading-relaxed whitespace-pre-wrap">{horse?.description}</p>
              </div>

              {/* Facts Table - Stile Travera */}
              {horse && (
                <div className="mt-8 md:mt-10" data-testid="facts-table">
                  <h2 className="font-display text-xl md:text-2xl mb-4">Fatti</h2>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <tbody>
                        <tr className="border-b border-gray-100">
                          <td className="py-3 px-4 text-[#64748b] text-sm bg-gray-50 w-1/3">Categoria</td>
                          <td className="py-3 px-4 font-medium text-sm">{typeLabel(horse.horse_type)}</td>
                        </tr>
                        {horse.pregnant_with && (
                          <tr className="border-b border-gray-100">
                            <td className="py-3 px-4 text-[#64748b] text-sm bg-gray-50">Gravida con</td>
                            <td className="py-3 px-4 font-medium text-sm">{horse.pregnant_with}</td>
                          </tr>
                        )}
                        {horse.expected_foaling && (
                          <tr className="border-b border-gray-100">
                            <td className="py-3 px-4 text-[#64748b] text-sm bg-gray-50">Data parto prevista</td>
                            <td className="py-3 px-4 font-medium text-sm">{horse.expected_foaling}</td>
                          </tr>
                        )}
                        <tr className="border-b border-gray-100">
                          <td className="py-3 px-4 text-[#64748b] text-sm bg-gray-50">Anno di nascita</td>
                          <td className="py-3 px-4 font-medium text-sm">{horse.birth_year || horse.age ? `${horse.birth_year || '?'} (${horse.age} anni)` : `${horse.age} anni`}</td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="py-3 px-4 text-[#64748b] text-sm bg-gray-50">Razza</td>
                          <td className="py-3 px-4 font-medium text-sm">{horse.breed}</td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="py-3 px-4 text-[#64748b] text-sm bg-gray-50">Colore</td>
                          <td className="py-3 px-4 font-medium text-sm">{horse.color}</td>
                        </tr>
                        {horse.height_cm && (
                          <tr className="border-b border-gray-100">
                            <td className="py-3 px-4 text-[#64748b] text-sm bg-gray-50">Altezza</td>
                            <td className="py-3 px-4 font-medium text-sm">{horse.height_cm} cm</td>
                          </tr>
                        )}
                        {horse.father && (
                          <tr className="border-b border-gray-100">
                            <td className="py-3 px-4 text-[#64748b] text-sm bg-gray-50">Padre</td>
                            <td className="py-3 px-4 font-medium text-sm">{horse.father}</td>
                          </tr>
                        )}
                        {horse.mother && (
                          <tr className="border-b border-gray-100">
                            <td className="py-3 px-4 text-[#64748b] text-sm bg-gray-50">Madre</td>
                            <td className="py-3 px-4 font-medium text-sm">{horse.mother}</td>
                          </tr>
                        )}
                        {horse.grandfather && (
                          <tr className="border-b border-gray-100">
                            <td className="py-3 px-4 text-[#64748b] text-sm bg-gray-50">Nonno materno</td>
                            <td className="py-3 px-4 font-medium text-sm">{horse.grandfather}</td>
                          </tr>
                        )}
                        {horse.registration_number && (
                          <tr className="border-b border-gray-100">
                            <td className="py-3 px-4 text-[#64748b] text-sm bg-gray-50">N. Registrazione</td>
                            <td className="py-3 px-4 font-medium text-sm">{horse.registration_number}</td>
                          </tr>
                        )}
                        <tr className="border-b border-gray-100">
                          <td className="py-3 px-4 text-[#64748b] text-sm bg-gray-50">Ubicazione</td>
                          <td className="py-3 px-4 font-medium text-sm">{horse.location}</td>
                        </tr>
                        {horse.external_link && (
                          <tr>
                            <td className="py-3 px-4 text-[#64748b] text-sm bg-gray-50">Link</td>
                            <td className="py-3 px-4 font-medium text-sm">
                              <a href={horse.external_link} target="_blank" rel="noopener noreferrer" className="text-[#c9a227] hover:underline">
                                {horse.external_link_label || horse.external_link}
                              </a>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Documents */}
              {horse?.documents?.length > 0 && (
                <div className="mt-8 md:mt-10" data-testid="documents-section">
                  <h2 className="font-display text-xl md:text-2xl mb-4">Documenti</h2>
                  <div className="space-y-3">
                    {horse.documents.map((doc, idx) => (
                      <a 
                        key={idx} 
                        href={doc.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        data-testid={`document-${idx}`}
                      >
                        <svg className="w-6 h-6 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z"/>
                        </svg>
                        <span className="text-sm font-medium text-[#0a1628] hover:text-[#c9a227]">{doc.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Seller Contact */}
              {horse?.seller_name && (
                <div className="mt-8 md:mt-10" data-testid="seller-section">
                  <h2 className="font-display text-xl md:text-2xl mb-4">Contatto</h2>
                  <div className="border border-gray-200 rounded-lg p-4 md:p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-[#64748b] text-xs uppercase tracking-wider mb-1">Venditore</p>
                        <p className="font-medium">{horse.seller_name}</p>
                      </div>
                      {horse.seller_contact && (
                        <div>
                          <p className="text-[#64748b] text-xs uppercase tracking-wider mb-1">Persona di contatto</p>
                          <p className="font-medium">{horse.seller_contact}</p>
                        </div>
                      )}
                      {horse.seller_email && (
                        <div>
                          <p className="text-[#64748b] text-xs uppercase tracking-wider mb-1">Email</p>
                          <a href={`mailto:${horse.seller_email}`} className="text-[#c9a227] hover:underline">{horse.seller_email}</a>
                        </div>
                      )}
                      {horse.seller_phone && (
                        <div>
                          <p className="text-[#64748b] text-xs uppercase tracking-wider mb-1">Telefono</p>
                          <a href={`tel:${horse.seller_phone}`} className="text-[#c9a227] hover:underline">{horse.seller_phone}</a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Share Buttons */}
              <div className="mt-8 md:mt-10" data-testid="share-section">
                <h2 className="font-display text-xl md:text-2xl mb-4">Condividi</h2>
                <div className="flex flex-wrap gap-3">
                  <a href={`https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-[#1877F2] text-white text-sm rounded hover:opacity-90">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z"/></svg>
                    Facebook
                  </a>
                  <a href={`https://wa.me/?text=${encodeURIComponent((horse?.name || '') + ' ' + shareUrl)}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-[#25D366] text-white text-sm rounded hover:opacity-90">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    WhatsApp
                  </a>
                  <a href={`https://x.com/intent/tweet/?text=${encodeURIComponent(horse?.name || '')}&url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-black text-white text-sm rounded hover:opacity-90">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    X
                  </a>
                </div>
              </div>
            </div>

            {/* RIGHT: Bidding Panel - DESKTOP ONLY (sticky) */}
            <div className="hidden lg:block lg:col-span-5">
              <div className="lg:sticky lg:top-24">
                <span className={auction.status === 'active' ? 'badge-live pulse-live' : auction.status === 'scheduled' ? 'badge-scheduled' : 'badge-ended'}>
                  {auction.status === 'active' ? 'LIVE' : auction.status === 'scheduled' ? 'PROSSIMA' : 'TERMINATA'}
                </span>

                {/* Price & Timer */}
                <div className="bg-[#0a1628] p-4 md:p-6 mt-4 mb-4 md:mb-6 rounded-lg">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-3 md:mb-4">
                    <span className="text-white/60 uppercase text-xs md:text-sm tracking-wider">Offerta attuale</span>
                    {auction.status === 'active' && (
                      <CountdownTimer endTime={auction.end_time} onEnd={fetchAuction} />
                    )}
                  </div>
                  <p className="text-3xl md:text-5xl font-bold text-[#c9a227]">{'\u20AC'}{auction.current_price?.toLocaleString()}</p>
                  <p className="text-white/60 text-xs md:text-sm mt-2">{auction.total_bids} offerte</p>
                </div>

                {/* Auction Ended Message */}
                {auction.status === 'ended' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 md:p-6 mb-4 md:mb-6 text-center" data-testid="auction-ended-message">
                    <p className="text-red-600 font-bold text-lg mb-1">ASTA TERMINATA</p>
                    <p className="text-red-500 text-sm">Non è più possibile fare offerte.</p>
                    {auction.winner_id && (
                      <p className="text-[#0a1628] text-sm mt-2 font-medium">Aggiudicata al miglior offerente</p>
                    )}
                  </div>
                )}

                {/* Bid Form - only when active */}
                {auction.status === 'active' && (
                  <div className="mb-4 md:mb-6" data-testid="bid-form">
                    {/* Bid Amount with +/- Buttons */}
                    <div className="flex items-center justify-center gap-3 mb-3">
                      <button
                        type="button"
                        onClick={decrementBid}
                        disabled={bidAmount <= minBid}
                        className="w-12 h-12 rounded-full border-2 border-gray-300 text-xl font-bold text-gray-600 hover:border-[#c9a227] hover:text-[#c9a227] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid="bid-decrement"
                      >
                        -
                      </button>
                      <div className="text-center px-4" data-testid="bid-amount-display">
                        <p className="text-2xl md:text-3xl font-bold text-[#0a1628]">{'\u20AC'}{bidAmount.toLocaleString()}</p>
                        <p className="text-xs text-[#64748b]">La tua offerta</p>
                      </div>
                      <button
                        type="button"
                        onClick={incrementBid}
                        className="w-12 h-12 rounded-full border-2 border-gray-300 text-xl font-bold text-gray-600 hover:border-[#c9a227] hover:text-[#c9a227] transition-colors"
                        data-testid="bid-increment"
                      >
                        +
                      </button>
                    </div>

                    {/* Place Bid Button */}
                    <button
                      onClick={() => {
                        if (!user) { window.location.href = '/login'; return; }
                        if (!user.is_verified) { setError('Account non verificato. Invia documenti a info@equusprime.it'); return; }
                        setShowConfirm(true);
                      }}
                      disabled={bidding || bidAmount < minBid}
                      className="w-full bg-[#0a7e8c] text-white py-3 md:py-4 rounded-lg font-bold text-base md:text-lg hover:bg-[#086a76] transition-colors disabled:opacity-50"
                      data-testid="bid-submit"
                    >
                      {bidding ? 'Invio...' : `Fai offerta a \u20AC${bidAmount.toLocaleString()}`}
                    </button>

                    {/* Autobid Section */}
                    {autobidActive ? (
                      <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between" data-testid="autobid-active">
                        <div>
                          <p className="text-green-700 text-sm font-medium">Autobid attivo</p>
                          <p className="text-green-600 text-xs">Fino a {'\u20AC'}{autobidActive.max_amount?.toLocaleString()}</p>
                        </div>
                        <button onClick={cancelAutobid} className="text-red-500 text-xs font-medium hover:underline" data-testid="autobid-cancel">Disattiva</button>
                      </div>
                    ) : (
                      <div className="mt-3">
                        {!showAutobid ? (
                          <button
                            onClick={() => setShowAutobid(true)}
                            className="w-full text-sm text-[#0a7e8c] font-medium py-2 border border-dashed border-[#0a7e8c] rounded-lg hover:bg-[#0a7e8c]/5 transition-colors"
                            data-testid="autobid-toggle"
                          >
                            Attiva Autobid (offerta automatica)
                          </button>
                        ) : (
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3" data-testid="autobid-form">
                            <p className="text-xs text-[#64748b] mb-2">Il sistema rilancerà automaticamente per te fino all'importo massimo.</p>
                            <div className="flex gap-2">
                              <input
                                type="number"
                                value={autobidMax}
                                onChange={e => setAutobidMax(e.target.value)}
                                placeholder="Importo massimo (€)"
                                className="input-field flex-1 text-sm"
                                min={minBid}
                                step="100"
                                data-testid="autobid-input"
                              />
                              <button onClick={handleAutobid} className="px-4 py-2 bg-[#0a7e8c] text-white rounded-lg text-sm font-medium hover:bg-[#086a76]" data-testid="autobid-confirm">
                                Attiva
                              </button>
                              <button onClick={() => setShowAutobid(false)} className="px-3 py-2 text-gray-500 text-sm hover:text-gray-700">
                                Annulla
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {error && <p className="text-red-500 text-sm mt-2" data-testid="bid-error">{error}</p>}
                    {!user && (
                      <p className="text-[#64748b] text-sm mt-2">
                        <a href="/login" className="text-[#c9a227]">Accedi</a> per fare un'offerta
                      </p>
                    )}
                  </div>
                )}

                {/* Bid History */}
                {auction.bids?.length > 0 && (
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="font-display text-lg mb-4">Storico Offerte</h3>
                    <div className="max-h-60 overflow-y-auto">
                      {auction.bids.map((bid, idx) => {
                        const isMe = user && bid.user_id === user.user_id;
                        const bidderColor = getBidderColor(bid.bidder_number);
                        return (
                        <div key={bid.bid_id} className="bid-item flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            {isMe ? (
                              <span className="w-6 h-6 rounded-full bg-[#c9a227] flex items-center justify-center flex-shrink-0" data-testid="bid-star-me">
                                <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
                              </span>
                            ) : (
                              <span className="w-6 h-6 rounded-full text-white text-[10px] flex items-center justify-center font-bold flex-shrink-0" style={{backgroundColor: bidderColor}}>
                                {bid.bidder_number || '?'}
                              </span>
                            )}
                            <div>
                              <p className="font-medium text-sm">{isMe ? 'Tu' : `Offerente ${bid.bidder_number || '?'}`}</p>
                              <p className="text-[#64748b] text-xs">{new Date(bid.created_at).toLocaleString('it-IT')}</p>
                            </div>
                          </div>
                          <span className="bid-amount">{'\u20AC'}{bid.amount.toLocaleString()}</span>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bid Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="bid-confirm-modal">
          <div className="bg-white rounded-2xl p-6 md:p-8 max-w-sm w-full shadow-2xl">
            <h3 className="font-display text-lg md:text-xl font-bold mb-2 text-center">Conferma Offerta</h3>
            <p className="text-[#64748b] text-sm text-center mb-4">
              Per completare l'offerta, conferma l'importo.
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-center">
              <p className="text-3xl font-bold text-[#0a1628]">{'\u20AC'}{bidAmount.toLocaleString()}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 py-3 border border-gray-300 rounded-lg text-gray-600 font-medium hover:bg-gray-50 transition-colors"
                data-testid="bid-confirm-cancel"
              >
                Annulla
              </button>
              <button
                onClick={handleBid}
                disabled={bidding}
                className="flex-1 py-3 bg-[#0a7e8c] text-white rounded-lg font-bold hover:bg-[#086a76] transition-colors disabled:opacity-50"
                data-testid="bid-confirm-ok"
              >
                {bidding ? 'Invio...' : 'Conferma'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Live Notification Banner */}
      {notification && (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-up" data-testid="bid-notification">
          <div className="bg-[#0a1628] text-white rounded-lg shadow-2xl p-4 flex items-center gap-3">
            <div className="w-8 h-8 bg-[#c9a227] rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold">Nuova offerta{notification.is_autobid ? ' (Auto)' : ''}</p>
              <p className="text-xs text-white/70 truncate">{notification.horse_name ? `${notification.horse_name} - ` : ''}Offerente {notification.bidder_number || '?'} - {'\u20AC'}{notification.amount.toLocaleString()}</p>
            </div>
            <button onClick={() => setNotification(null)} className="text-white/50 hover:text-white">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuctionDetailPage;
